import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import {
  Button,
  Modal,
  ScrollAreaAutosize,
  Select,
  Text,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { IconFileUpload, IconInfoCircle } from "@tabler/icons-react";
import i18n from "src/i18n";
import { z } from "zod";

import { getClinicFileTypes } from "@data/dropdownValues";

import { documentFileTypes, imageFileTypes } from "@constants/fileTypes";

import { useUpdateClinicFile } from "@hooks/mutations/clinic/useUpdateClinicFile";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";

import { ClinicFile } from "@customTypes/clinic";

import { StyledAddFileModalContent } from "../AddFileModal/AddFileModal.styled";


const formSchema = () =>
  z.object({
    tag: z.string().min(1, i18n.t("tag-required")),
    targetUser: z.string().optional(),
  });

type FormInputs = z.infer<ReturnType<typeof formSchema>>;

interface EditFileModalProps {
  opened: boolean;
  close: () => void;
  file: ClinicFile | null;
}

export const EditFileModal = ({
  opened,
  close,
  file,
}: EditFileModalProps) => {
  const { t } = useTranslation();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const { data: doctorsData } = useGetUsers({ role: "doctor", accountOptions: true });
  const {
    mutate: updateClinicFile,
    isLoading: isUpdateClinicFileLoading,
  } = useUpdateClinicFile();

  const doctorsComboboxItems = useMemo(() => {
    return doctorsData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [doctorsData]);

  const form = useForm({
    mode: "uncontrolled",
    validate: zodResolver(formSchema()),
    initialValues: {
      tag: "",
      targetUser: "",
    },
  });

  // Populate form when file changes
  useEffect(() => {
    if (file && opened) {
      form.setValues({
        tag: file.tag || "",
        targetUser: file.assignedDoctor || "",
      });
      setUploadedFiles([]); // Reset uploaded files when opening modal
    }
  }, [file, opened]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([
        ...uploadedFiles,
        ...acceptedFiles.map(file => {
          const blob = file.slice(0, file.size, file.type);
          const newFile = new File([blob], file.name, {
            type: file.type,
          });
          return newFile;
        }),
      ]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": imageFileTypes,
      "application/pdf": documentFileTypes,
    },
    onDrop,
    multiple: false,
    maxFiles: 1,
    maxSize: 3 * 1024 * 1024, // 3MB max
    disabled: uploadedFiles.length > 0,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const handleFormSubmit = (values: FormInputs) => {
    if (!file) return;

    const formData = new FormData();

    // Add file if a new one was uploaded
    if (uploadedFiles.length > 0) {
      uploadedFiles.forEach(uploadedFile => {
        formData.append("file", uploadedFile);
      });
    }

    formData.append("type", values.tag);
    if (values.targetUser) {
      formData.append("related_user", values.targetUser);
    }

    updateClinicFile(
      {
        fileId: file.id,
        fileData: formData,
      },
      {
        onSuccess: () => {
          form.reset();
          setUploadedFiles([]);
          close();
        },
      }
    );
  };

  const handleClose = () => {
    form.reset();
    setUploadedFiles([]);
    close();
  };

  const fileTagOptions = getClinicFileTypes();

  if (!file) return null;

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("edit-file")}</Text>}
      opened={opened}
      onClose={handleClose}
      scrollAreaComponent={ScrollAreaAutosize}
      size="md"
      styles={{
        body: {
          maxWidth: '100%',
          overflowX: 'hidden',
        },
      }}>
      <StyledAddFileModalContent>
        <form
          className="form"
          onSubmit={form.onSubmit(
            values => handleFormSubmit(values),
            errors => {
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}>

          {/* Current File Info */}
          <div className="form-field">
            <Text size="sm" fw={500} mb="xs">
              {t("current-file")}
            </Text>
            <Text size="sm" c="dimmed">
              {file.fileName}
            </Text>
          </div>

          <div className="form-field">
            <Select
              label={t("file-tag")}
              withAsterisk
              placeholder={t("select-file-tag")}
              data={fileTagOptions}
              {...form.getInputProps("tag")}
              key={form.key("tag")}
            />
          </div>

          <div className="form-field">
            <Select
              label={t("assign-to-doctor")}
              placeholder={t("select-doctor")}
              clearable
              data={doctorsComboboxItems}
              {...form.getInputProps("targetUser")}
              key={form.key("targetUser")}
            />
          </div>

          {/* File Upload Section */}
          <div className="form-field">
            <Text size="sm" fw={500} mb="xs">
              {t("replace-file-optional")}
            </Text>
          </div>

          <div className="files-wrapper">
            <div
              {...getRootProps({
                className: `dropzone ${isDragActive ? "active" : ""} ${
                  isDragAccept ? "accept" : ""
                } ${isDragReject ? "reject" : ""}`,
              })}>
              <input {...getInputProps()} />
              <div className="text-content">
                <div className="svg-wrapper">
                  <IconFileUpload size={28} />
                </div>
                <div className="p-wrapper">
                  <p>{t("drag-file-here")}</p>
                  <p>{t("or-click-to-browse-max")}</p>
                </div>
              </div>
            </div>

            {uploadedFiles.length > 0 && (
              <div className="uploaded-files">
                {uploadedFiles.map((uploadedFile, index) => (
                  <div key={index} className="uploaded-file">
                    <div className="file-info">
                      <div className="file-details">
                        <Text size="sm" fw={500}>{uploadedFile.name}</Text>
                        <Text size="xs" c="dimmed">
                          {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
                        </Text>
                      </div>
                      <Button
                        variant="subtle"
                        size="xs"
                        color="red"
                        onClick={() => removeFile(uploadedFile)}>
                        {t("remove")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {uploadedFiles.length == 0 && (
              <div className="info-section">
                <div className="info-icon">
                  <IconInfoCircle size={20} />
                </div>
                <p className="info-text">
                  {t("file-replace-optional-info")}
                </p>
              </div>
            )}
          </div>

          <div className="form-actions">
            <PermissionGate permission="clinic_files.edit"><Button
              type="submit"
              loading={isUpdateClinicFileLoading}
              disabled={isUpdateClinicFileLoading}>
              {t("submit")}
            </Button></PermissionGate>
          </div>
        </form>
      </StyledAddFileModalContent>
    </Modal>
  );
};
